import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Batch, DataItem, DeliveryBatchStatus, DeliveryStatus, FraudStatus, QcStatus } from "../store/types"
import { fmtBytes, fmtDuration, replayUrl } from "../utils"
import { ContractSettings } from "./contract-settings"
import { SelectionPlanner } from "./selection-planner"

const BATCH_COLOR: Record<DeliveryBatchStatus, string> = {
  RESERVED: "blue",
  BUILDING: "gold",
  READY: "cyan",
  DELIVERED: "green",
  FAILED: "red",
  CANCELLED: "default",
  DISPUTED: "red",
}

const VERDICT_COLOR: Record<string, string> = { PENDING: "default", PASSED: "green", FAILED: "red" }
const DELIVERY_COLOR: Record<DeliveryStatus, string> = { AVAILABLE: "default", RESERVED: "blue", DELIVERED: "cyan" }
const VERDICTS = ["PASSED", "PENDING", "FAILED"].map(v => ({ value: v, label: v }))
const DELIVERIES = ["AVAILABLE", "RESERVED", "DELIVERED"].map(v => ({ value: v, label: v }))
const HOUR = 3600_000
const DAY = 24 * HOUR
const TIME_PRESETS: Array<{ label: string; ms: number }> = [
  { label: "1h", ms: HOUR },
  { label: "6h", ms: 6 * HOUR },
  { label: "24h", ms: DAY },
  { label: "3d", ms: 3 * DAY },
  { label: "7d", ms: 7 * DAY },
  { label: "14d", ms: 14 * DAY },
]

export const DeliveryComposer: FC = observer(() => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const navigate = useNavigate()
  const { id: contractId = "" } = useParams<{ id: string }>()

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [preset, setPreset] = useState<string>("")
  const [filters, setFilters] = useState<{
    gameId?: number
    accountId?: string
    accountEmail?: string
    recordingId?: string
    fraudStatus?: FraudStatus
    qcStatus?: QcStatus
    deliveryStatus?: DeliveryStatus
    tags?: string[]
    utmSource?: string
    recordedFrom?: string
    recordedTo?: string
    deliverableOnly?: boolean
  }>({ deliverableOnly: true })

  // Only the contract the URL asks for. `selectedContract` still holds the
  // PREVIOUS one until loadContract resolves, and handing that to the tabs lets
  // the settings form PATCH the old contract and the planner list its selections.
  const contract = store.selectedContract?.id === contractId ? store.selectedContract : null
  const clientId = contract?.clientId ?? ""

  useEffect(() => {
    // The composer stays mounted across contract URLs, so the previous
    // contract's checked rows and summary would otherwise survive — and
    // `reserve()` would post them against the NEW contractId.
    setSelectedIds([])
    store.clearSummary()
    store.loadContract(contractId)
    store.loadGames()
    store.loadFacets()
    store.loadBatches(contractId)
  }, [store, contractId])

  useEffect(() => {
    // `clientId` is empty until the contract the URL asks for is the one loaded
    // (see above), so this cannot fire as loadBalance(staleClientId, newContractId)
    // — which 404s "Contract not found" and surfaces as a 500.
    if (clientId) {
      store.loadBalance(clientId, contractId)
      store.searchItems({ contractId, deliverableOnly: true, limit: 200 })
    }
  }, [store, clientId, contractId, contract?.id])

  const applyFilters = () => {
    if (!clientId) return
    store.searchItems({ contractId, ...filters, limit: 200 })
  }

  const applyPreset = (label: string, ms: number) => {
    // Clicking the active preset toggles it off → back to unfiltered dates.
    if (preset === label) {
      setPreset("")
      setFilters(f => ({ ...f, recordedFrom: undefined, recordedTo: undefined }))

      return
    }
    const to = new Date()
    const from = new Date(to.getTime() - ms)
    setPreset(label)
    setFilters(f => ({ ...f, recordedFrom: from.toISOString(), recordedTo: to.toISOString() }))
  }

  const onSelectChange = (keys: React.Key[]) => {
    const ids = keys as string[]
    setSelectedIds(ids)
    store.summarize(clientId, contractId, ids)
  }

  const reserve = async() => {
    const batch = await store.execute({ clientId, contractId, itemIds: selectedIds })
    if (batch) {
      navigate(`/data-delivery/deliveries/${batch.id}`)
    }
  }

  // Bulk mode — server reserves the entire eligible set matching the ACTIVE filter
  // (no row selection), builds async. Eligibility gates still apply on top.
  const deliverAll = async() => {
    const batch = await store.execute({
      clientId,
      contractId,
      deliverAll: true,
      filters: {
        gameId: filters.gameId,
        accountId: filters.accountId,
        accountEmail: filters.accountEmail,
        recordingId: filters.recordingId,
        utmSource: filters.utmSource,
        tags: filters.tags,
        recordedFrom: filters.recordedFrom,
        recordedTo: filters.recordedTo,
      },
    })
    if (batch) {
      navigate(`/data-delivery/deliveries/${batch.id}`)
    }
  }

  const set = (patch: Partial<typeof filters>) => setFilters({ ...filters, ...patch })
  const mono = (v: string) => <span style={{ fontFamily: "monospace" }}>{v}</span>
  const verdictTag = (s: string) => <Tag color={VERDICT_COLOR[s]}>{s}</Tag>
  const tagsRender = (t: string[]) => (t.length ? t.map(x => <Tag key={x} color="blue">{x}</Tag>) : "—")
  const cmpStr = (a: string | null, b: string | null) => (a ?? "").localeCompare(b ?? "")
  const columns = [
    {
      title: "Recording",
      dataIndex: "recordingId",
      key: "recordingId",
      sorter: (a: DataItem, b: DataItem) => cmpStr(a.recordingId, b.recordingId),
      render: (v: string) => (
        <Space size={4}>
          {mono(v)}
          <Typography.Text copyable={{ text: v }} />
        </Space>
      ),
    },
    {
      title: "Game",
      key: "game",
      sorter: (a: DataItem, b: DataItem) => a.gameId - b.gameId,
      render: (_: unknown, r: DataItem) => (r.gameName ? `${r.gameId} · ${r.gameName}` : r.gameId),
    },
    {
      title: "Account",
      dataIndex: "accountId",
      key: "accountId",
      sorter: (a: DataItem, b: DataItem) => cmpStr(a.accountId, b.accountId),
      render: (v: string | null) => (v ? v.slice(0, 8) + "…" : "—"),
    },
    {
      title: "Recorded",
      dataIndex: "recordedAt",
      key: "recordedAt",
      defaultSortOrder: "descend" as const,
      sorter: (a: DataItem, b: DataItem) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime(),
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: "Length",
      dataIndex: "durationSec",
      key: "durationSec",
      sorter: (a: DataItem, b: DataItem) => (a.durationSec ?? 0) - (b.durationSec ?? 0),
      render: (v: number | null) => (v == null ? "—" : fmtDuration(v)),
    },
    {
      title: "Size",
      dataIndex: "byteSize",
      key: "byteSize",
      sorter: (a: DataItem, b: DataItem) => Number(a.byteSize) - Number(b.byteSize),
      render: (v: string) => fmtBytes(v),
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      sorter: (a: DataItem, b: DataItem) => cmpStr(a.region, b.region),
      render: (v: string | null) => v ?? "—",
    },
    {
      title: "Fraud",
      dataIndex: "fraudStatus",
      key: "fraudStatus",
      sorter: (a: DataItem, b: DataItem) => cmpStr(a.fraudStatus, b.fraudStatus),
      render: verdictTag,
    },
    {
      title: "QC",
      dataIndex: "qcStatus",
      key: "qcStatus",
      sorter: (a: DataItem, b: DataItem) => cmpStr(a.qcStatus, b.qcStatus),
      render: verdictTag,
    },
    {
      title: "Delivery",
      dataIndex: "deliveryStatus",
      key: "deliveryStatus",
      sorter: (a: DataItem, b: DataItem) => cmpStr(a.deliveryStatus, b.deliveryStatus),
      render: (s: DeliveryStatus) => <Tag color={DELIVERY_COLOR[s]}>{s}</Tag>,
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      sorter: (a: DataItem, b: DataItem) => a.tags.length - b.tags.length,
      render: tagsRender,
    },
    {
      title: "",
      key: "replay",
      render: (_: unknown, r: DataItem) => (
        <a href={replayUrl(r.s3Prefix)} target="_blank" rel="noreferrer">▶ Replay</a>
      ),
    },
  ]

  const summary = store.summary
  const remaining = store.balance?.remaining ?? null
  const overDelivered = store.balance?.overDelivered ?? "0"
  const quotaUnit = store.balance?.quotaUnit ?? "ITEMS"
  const isHours = quotaUnit === "SECONDS"
  const unitLabel = isHours ? "hours" : "items"
  const fmtQ = (v: string | null) => (v == null ? "—" : isHours ? `${Math.round(Number(v) / 3600)} h` : v)

  // Aggregate totals across DELIVERED batches — the consumed (consumed == delivered) footprint.
  const deliveredBatches = store.batches.filter(b => b.status === "DELIVERED")
  const totalDelivered = {
    recordings: deliveredBatches.reduce((s, b) => s + b.itemCount, 0),
    seconds: deliveredBatches.reduce((s, b) => s + Number(b.reservedSeconds || 0), 0),
    bytes: deliveredBatches.reduce((s, b) => s + Number(b.totalBytes || 0), 0),
  }

  // Quota is a soft floor — a selection beyond remaining over-delivers (allowed), it never blocks.
  const overBy = (() => {
    if (!summary || summary.fits || remaining == null) return null
    const selected = isHours ? summary.selection.durationSec : summary.selection.count
    const over = selected - Number(remaining)

    return over > 0 ? fmtQ(String(over)) : null
  })()

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/data-delivery/contracts">Contracts</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{contract ? contract.name : `${contractId.slice(0, 8)}…`}</Breadcrumb.Item>
      </Breadcrumb>
      <Card
        title={
          <Space size={8}>
            <span>Contract</span>
            <Typography.Text copyable={{ text: contractId }} />
            {contract ? <span>· {contract.name}</span> : null}
          </Space>
        }
      >
        <Row gutter={24}>
          <Col><Statistic title={`Quota (${unitLabel})`} value={fmtQ(store.balance?.quota ?? null)} /></Col>
          <Col><Statistic title="Delivered" value={fmtQ(store.balance?.delivered ?? "0")} valueStyle={{ color: "#52c41a" }} /></Col>
          <Col><Statistic title="Reserved" value={fmtQ(store.balance?.reserved ?? "0")} valueStyle={{ color: "#faad14" }} /></Col>
          <Col><Statistic title="Remaining" value={fmtQ(remaining)} /></Col>
          {overDelivered !== "0" && (
            <Col><Statistic title="Over-delivered" value={fmtQ(overDelivered)} valueStyle={{ color: "#1677ff" }} /></Col>
          )}
          <Col flex="auto" style={{ minWidth: 200, alignSelf: "center" }}>
            <Progress
              percent={store.balance && store.balance.quota
                ? Math.min(100, Math.round((Number(store.balance.delivered) / Number(store.balance.quota)) * 100))
                : 0}
            />
          </Col>
        </Row>
      </Card>

      {/* Three ways into a delivery, kept as tabs rather than stacked cards:
          the co-play planner and the manual composer are alternative routes to
          the same reserve, and the page is already long. */}
      <Tabs
        // Keyed on the loaded contract: defaultActiveKey is read once at mount,
        // and this page mounts while the contract is still loading — so without
        // a remount a co-play contract would open on the manual composer.
        key={contract?.id ?? "loading"}
        defaultActiveKey={contract?.cutConfig?.cutMode === "multiplayer" || contract?.storageConfig?.includeSessionManifest
          ? "coplay"
          : "manual"}
        items={[
          {
            key: "coplay",
            label: "Co-play selection",
            children: contract
              ? <SelectionPlanner contract={contract} />
              : <Card size="small" loading />,
          },
          {
            key: "manual",
            label: "Manual composer",
            children: (
              <Space direction="vertical" style={{ width: "100%" }} size={16}>
                <Card
                  title="Recordings — Delivery Composer"
                  extra={(
                    <span>
                      <b>{store.dataItemsTotal.toLocaleString()}</b> recordings ·{" "}
                      <b>{fmtDuration(store.dataItemsSeconds)}</b> of content match the filter
                    </span>
                  )}
                >
                  {store.dataItemsStaleReason && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Showing a stale pool — the recording index could not be refreshed"
                      description={(
                        <>
                          These rows are whatever the ledger already held, so an empty or short list here does not mean the
                          game has no recordings. Reason: <Typography.Text code>{store.dataItemsStaleReason}</Typography.Text>
                        </>
                      )}
                    />
                  )}
                  <div style={{ marginBottom: 16 }}>
                    <Row gutter={12} align="middle" style={{ marginBottom: 12 }}>
                      <Col flex="auto">
                        <Select
                          showSearch
                          allowClear
                          style={{ width: "100%" }}
                          placeholder="🔍 Search game by name"
                          value={filters.gameId}
                          filterOption={false}
                          onSearch={v => store.loadGames(v || undefined)}
                          onChange={v => set({ gameId: v ?? undefined })}
                          options={store.games.map(g => ({ value: g.gameId, label: `${g.gameId} · ${g.gameName} (${g.count})` }))}
                        />
                      </Col>
                      <Col>
                        <Space size={4}>
                          {TIME_PRESETS.map(p => (
                            <Button
                              key={p.label}
                              shape="round"
                              type={preset === p.label ? "primary" : "default"}
                              onClick={() => applyPreset(p.label, p.ms)}
                            >
                              {p.label}
                            </Button>
                          ))}
                          <Button
                            shape="round"
                            type={preset === "Custom" ? "primary" : "default"}
                            onClick={() => setPreset("Custom")}
                          >
                  Custom
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                    {preset === "Custom" && (
                      <Row style={{ marginBottom: 12 }}>
                        <DatePicker.RangePicker
                          showTime
                          onChange={(vals: any) => {
                            set({
                              recordedFrom: vals?.[0]?.toISOString?.() || undefined,
                              recordedTo: vals?.[1]?.toISOString?.() || undefined,
                            })
                          }}
                        />
                      </Row>
                    )}
                    <Row gutter={12} wrap>
                      <Col>
                        <Input
                          addonBefore="Email"
                          style={{ width: 240 }}
                          value={filters.accountEmail}
                          onChange={e => set({ accountEmail: e.target.value || undefined })}
                          placeholder="player@example.com"
                        />
                      </Col>
                      <Col>
                        <Input
                          addonBefore="Account id"
                          style={{ width: 280 }}
                          value={filters.accountId}
                          onChange={e => set({ accountId: e.target.value || undefined })}
                          placeholder="UUID (alternative to email)"
                        />
                      </Col>
                      <Col flex="auto">
                        <Input
                          value={filters.recordingId}
                          onChange={e => set({ recordingId: e.target.value || undefined })}
                          placeholder="Recording id, name, or S3 prefix"
                        />
                      </Col>
                    </Row>
                  </div>

                  <Form layout="inline" style={{ marginBottom: 16, rowGap: 8 }}>
                    <Form.Item
                      label="Fraud"
                      tooltip={filters.deliverableOnly ? "Locked to PASSED while “Deliverable only” is on" : undefined}
                    >
                      <Select
                        style={{ width: 120 }}
                        allowClear
                        disabled={filters.deliverableOnly}
                        value={filters.deliverableOnly ? "PASSED" : filters.fraudStatus}
                        onChange={v => set({ fraudStatus: v })}
                        options={VERDICTS}
                        placeholder="any"
                      />
                    </Form.Item>
                    <Form.Item
                      label="QC"
                      tooltip={filters.deliverableOnly ? "Locked to PASSED while “Deliverable only” is on" : undefined}
                    >
                      <Select
                        style={{ width: 120 }}
                        allowClear
                        disabled={filters.deliverableOnly}
                        value={filters.deliverableOnly ? "PASSED" : filters.qcStatus}
                        onChange={v => set({ qcStatus: v })}
                        options={VERDICTS}
                        placeholder="any"
                      />
                    </Form.Item>
                    <Form.Item label="Delivery">
                      <Select
                        style={{ width: 130 }}
                        allowClear
                        value={filters.deliveryStatus}
                        onChange={v => set({ deliveryStatus: v })}
                        options={DELIVERIES}
                        placeholder="any"
                      />
                    </Form.Item>
                    <Form.Item label="Tags">
                      <Select
                        mode="tags"
                        style={{ minWidth: 140 }}
                        value={filters.tags}
                        onChange={v => set({ tags: v })}
                        placeholder="any"
                      />
                    </Form.Item>
                    <Form.Item label="UTM source" tooltip="Acquisition source of the player who produced the recording">
                      <Select
                        showSearch
                        allowClear
                        style={{ minWidth: 160 }}
                        value={filters.utmSource}
                        onChange={v => set({ utmSource: v ?? undefined })}
                        options={store.facets.utmSources.map(u => ({ value: u, label: u }))}
                        placeholder="any"
                      />
                    </Form.Item>
                    <Form.Item
                      label="Deliverable only"
                      tooltip={
                        "Show only recordings you can deliver right now: COMPLETE + fraud PASSED + QC PASSED, "
              + "and not already delivered to this contract. Turn off to also see pending/failed or "
              + "already-delivered recordings (selecting a non-deliverable one is rejected on reserve)."
                      }
                    >
                      <Switch checked={filters.deliverableOnly} onChange={v => setFilters({ ...filters, deliverableOnly: v })} />
                    </Form.Item>
                    <Form.Item>
                      <Button type="primary" onClick={applyFilters}>Apply</Button>
                    </Form.Item>
                  </Form>

                  <Table
                    rowKey="id"
                    size="small"
                    loading={store.isLoading}
                    columns={columns}
                    dataSource={store.dataItems}
                    rowSelection={{
                      selectedRowKeys: selectedIds,
                      onChange: onSelectChange,
                      // Header checkbox selects the current page; the dropdown adds
                      // "Select all data" to pick every loaded recording across pages.
                      selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
                    }}
                    pagination={{ pageSize: 20 }}
                  />

                  <Card size="small" style={{ marginTop: 12, borderTop: "2px solid #1677ff" }}>
                    <Row gutter={24} align="middle">
                      <Col><Statistic title="Selected" value={summary?.selection.count ?? selectedIds.length} /></Col>
                      <Col><Statistic title="Σ Length" value={summary ? fmtDuration(summary.selection.durationSec) : "—"} /></Col>
                      <Col><Statistic title="Σ Size" value={summary ? fmtBytes(summary.selection.bytes) : "—"} /></Col>
                      <Col><Statistic title={`vs Remaining (${unitLabel})`} value={fmtQ(remaining)} /></Col>
                      <Col>
                        <Statistic
                          title="Previously delivered to client"
                          value={summary
                            ? `${summary.previouslyDelivered.count} · ${fmtDuration(summary.previouslyDelivered.durationSec)}`
                            : "—"}
                        />
                      </Col>
                      <Col flex="auto" style={{ textAlign: "right" }}>
                        <Space>
                          {summary && (summary.fits
                            ? <Alert type="success" showIcon message="Within quota" />
                            : <Alert type="info" showIcon message={overBy ? `Over-delivers by ${overBy}` : "Over-delivers (allowed)"} />)}
                          <Button
                            onClick={deliverAll}
                            title="Reserve every eligible recording matching the current filter (ignores row selection)"
                          >
                  Deliver all eligible →
                          </Button>
                          <Button type="primary" disabled={selectedIds.length === 0} onClick={reserve}>Reserve &amp; Build →</Button>
                        </Space>
                      </Col>
                    </Row>
                  </Card>
                </Card>

                <Card title={`Deliveries — history (${store.batches.length})`} size="small">
                  <Row gutter={24} style={{ marginBottom: 12 }}>
                    <Col><Statistic title="Total delivered — recordings" value={totalDelivered.recordings} /></Col>
                    <Col><Statistic title="Total delivered — hours" value={fmtDuration(totalDelivered.seconds)} /></Col>
                    <Col><Statistic title="Total delivered — bytes" value={fmtBytes(String(totalDelivered.bytes))} /></Col>
                  </Row>
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={{ pageSize: 10 }}
                    dataSource={store.batches}
                    columns={[
                      {
                        title: "Delivery",
                        dataIndex: "id",
                        key: "id",
                        render: (id: string) => (
                          <Link to={`/data-delivery/deliveries/${id}`} style={{ fontFamily: "monospace" }}>
                            {id.slice(0, 8)}…
                          </Link>
                        ),
                      },
                      {
                        title: "Status",
                        dataIndex: "status",
                        key: "status",
                        render: (s: DeliveryBatchStatus) => <Tag color={BATCH_COLOR[s]}>{s}</Tag>,
                      },
                      { title: "Items", dataIndex: "itemCount", key: "itemCount" },
                      {
                        title: "Delivery hours",
                        dataIndex: "reservedSeconds",
                        key: "reservedSeconds",
                        render: (sec: string) => fmtDuration(Number(sec || 0)),
                      },
                      {
                        title: "Delivered / reserved",
                        key: "when",
                        render: (_: unknown, r: Batch) => new Date(r.deliveredAt ?? r.reservedAt).toLocaleString(),
                      },
                    ]}
                  />
                </Card>
              </Space>
            ),
          },
          {
            key: "settings",
            label: "Contract settings",
            children: contract
              ? <ContractSettings contract={contract} />
              : <Card size="small" loading />,
          },
        ]}
      />
    </Space>
  )
})
